// A request that can be given up on for two reasons at once.
//
// ofetch honours its own `timeout` option only when the caller passes no signal
// (`if (!context.options.signal && context.options.timeout)`, ofetch 1.5.1). So the
// moment a request is made cancellable it silently loses its ceiling, and a scan sent
// with no connection would wait for ever instead of settling as unreachable. That is
// the one thing the upload path must not do, which is why the ceiling is rebuilt here
// rather than left to the library.
//
// The caller cannot tell the two reasons apart, deliberately. An abandoned send costs
// the scan nothing either way: the record is already on the phone and goes up on the
// next drain under the same scan_uuid, which the server collapses onto the same lead.
//
//   yarn test
export interface BoundedSignal {
  signal: AbortSignal

  // Drops the timer and the listener. Always called once the request has settled, or a
  // fast answer leaves a timer running for the rest of its ceiling and a listener on a
  // signal that outlives the request it belonged to.
  release: () => void
}

export const boundedSignal = (timeoutMs: number, cancel?: AbortSignal): BoundedSignal => {
  const controller = new AbortController()

  // Already given up on before the request was even made. Nothing to wire up, and
  // nothing to release.
  if (cancel?.aborted) {
    controller.abort()

    return { signal: controller.signal, release: () => {} }
  }

  const abort = () => controller.abort()
  const timer = setTimeout(abort, timeoutMs)

  cancel?.addEventListener('abort', abort, { once: true })

  return {
    signal: controller.signal,
    release: () => {
      clearTimeout(timer)
      cancel?.removeEventListener('abort', abort)
    },
  }
}
